import React, { useEffect, useState } from "react";
import { IoArrowForward, IoClose } from "react-icons/io5";
import { LuMail } from "react-icons/lu";
import { ReactComponent as LeadershipIllustration } from "../../assets/Illustrations/Leadership 3 Streamline Milano.svg";

const HOST_EMAIL = "careers@jointribe.io";

function buildMailto() {
  const subject = "Become a Host — Inquiry";
  const body = `Hi Tribe team,

I’m interested in hosting gatherings with Tribe.

• Name:
• City:
• Types of gatherings I’d like to host:
• Relevant experience:
• Links (IG/Website):

Thanks!`;

  return `mailto:${HOST_EMAIL}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
}

export default function HostWithTribeSection() {
  const [showHostSheet, setShowHostSheet] = useState(false);

  const openHostSheet = () => setShowHostSheet(true);
  const closeHostSheet = () => setShowHostSheet(false);

  const openEmail = () => {
    const href = buildMailto();
    const a = document.createElement("a");
    a.href = href;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 0);
  };

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');

    if (!showHostSheet) {
      if (meta) meta.content = "#ffffff";
      return;
    }

    if (meta) meta.content = "#999998";

    return () => {
      if (meta) meta.content = "#ffffff";
    };
  }, [showHostSheet]);

  return (
    <>
      <section className="hostWithTribeSection" style={styles.hostSection}>
        <div style={styles.hostInner}>
          <p style={styles.sectionEyebrow}>host with tribe</p>

          <h2 className="hostTitle" style={styles.hostTitle}>
            want to bring people together?
          </h2>

          <p className="hostSub" style={styles.hostSub}>
            Our hosts help make gatherings feel welcoming.
            If you like helping people connect, apply to become a host.
          </p>

          <button type="button" style={styles.hostLink} onClick={openHostSheet}>
            become a host
            <IoArrowForward style={styles.arrowIcon} />
          </button>
        </div>
      </section>

      {showHostSheet && (
        <div
          style={styles.backdrop}
          className="host-sheet-backdrop"
          onClick={closeHostSheet}
        >
          <div
            role="dialog"
            aria-modal="true"
            style={styles.sheet}
            className="host-sheet-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.sheetTopBar}>
              <div style={styles.sheetTopTitle}>Become a Host</div>

              <button
                type="button"
                aria-label="Close"
                style={styles.iconBtn}
                onClick={closeHostSheet}
              >
                <IoClose size={18} style={{ display: "block" }} />
              </button>
            </div>

            <div style={styles.sheetContent}>
              <LeadershipIllustration style={styles.largeSvg} />

              <h2 style={styles.sheetTitle}>Become a Host</h2>

              <p style={styles.sheetSubtitle}>
                We’re looking for people who want to bring others together
                around unique in-person experiences.
              </p>

              <ul style={styles.points}>
                <li>Lead structured, in-person gatherings</li>
                <li>Help people feel welcomed and included</li>
                <li>Earn for the gatherings you host</li>
              </ul>

              <button type="button" style={styles.actionButton} onClick={openEmail}>
                <LuMail style={styles.mailIcon} />
                Email us to apply
              </button>

              <button
                type="button"
                style={styles.dismissButton}
                onClick={closeHostSheet}
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          .hostWithTribeSection {
            display: none;
          }

          @media (max-width: 450px) {
            .hostWithTribeSection {
              display: block !important;
            }

            .hostTitle {
              margin: 10px 0 0 !important;
              font-size: clamp(2.05rem, 8.6vw, 2.55rem) !important;
              line-height: 1.08 !important;
              letter-spacing: -0.015em !important;
              font-weight: 400 !important;
              text-transform: lowercase !important;
              color: #171717 !important;
            }

            .hostSub {
              max-width: 340px !important;
              margin: 14px 0 0 !important;
              font-size: 0.94rem !important;
              line-height: 1.52 !important;
              color: #555 !important;
              font-weight: 430 !important;
              letter-spacing: -0.012em !important;
            }
          }

          @keyframes hostSheetFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes hostSheetUp {
            from {
              transform: translateY(32px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }

          .host-sheet-backdrop {
            animation: hostSheetFadeIn .24s ease-out;
          }

          .host-sheet-card {
            animation: hostSheetUp .34s cubic-bezier(.19,1,.22,1);
          }
        `}
      </style>
    </>
  );
}

const styles = {
  hostSection: {
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    background: "#fff",
    color: "#171717",
    borderTop: "1px solid rgba(0,0,0,0.08)",
  },

  hostInner: {
    maxWidth: "1080px",
    margin: "0 auto",
    padding: "64px 22px 70px",
  },

  sectionEyebrow: {
    margin: 0,
    fontFamily: '"Ugly Handwriting", cursive',
    fontSize: "1.25rem",
    fontWeight: 500,
    lineHeight: 1.15,
    letterSpacing: "normal",
    textTransform: "lowercase",
    color: "#343c54",
  },

  hostTitle: {
    fontFamily: '"Instrument Serif", Georgia, serif',
    fontStyle: "normal",
    maxWidth: "760px",
    margin: "12px 0 0",
    color: "#171717",
    fontSize: "clamp(2rem, 3.7vw, 2.8rem)",
    lineHeight: 1.08,
    fontWeight: 400,
    letterSpacing: "-0.015em",
    textTransform: "none",
  },

  hostSub: {
    maxWidth: "390px",
    margin: "16px 0 0",
    color: "#555",
    fontSize: "0.98rem",
    lineHeight: 1.52,
    fontWeight: 430,
    letterSpacing: "-0.012em",
  },

  hostLink: {
    fontFamily: "inherit",
    marginTop: "24px",
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#171717",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "0.96rem",
    fontWeight: 680,
    letterSpacing: "-0.015em",
    textTransform: "lowercase",
    borderBottom: "1px solid rgba(0,0,0,0.28)",
    paddingBottom: "3px",
    WebkitTapHighlightColor: "transparent",
  },

  arrowIcon: {
    fontSize: "0.98rem",
    transform: "translateY(1px)",
  },

  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",
    zIndex: 2000,
  },

  sheet: {
    width: "96%",
    maxWidth: 420,
    background: "#fff",
    borderRadius: 12,
    padding: 0,
    margin: "0 auto 12px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    textAlign: "center",
    overflow: "hidden",
  },

  sheetTopBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: 48,
    padding: "0 16px",
    borderBottom: "1px solid #eef0f2",
  },

  sheetTopTitle: {
    fontSize: 14,
    fontWeight: 650,
    color: "#0b0b0b",
    letterSpacing: "-0.01em",
  },

  iconBtn: {
    background: "transparent",
    border: "1px solid #e5e7eb",
    width: 32,
    height: 32,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
    padding: 0,
    borderRadius: 8,
    color: "#0b0b0b",
    cursor: "pointer",
  },

  sheetContent: {
    padding: 24,
    paddingBottom: "calc(24px + env(safe-area-inset-bottom, 0px))",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  largeSvg: {
    width: 220,
    height: "auto",
    marginBottom: 0,
    display: "block",
  },

  sheetTitle: {
    margin: 0,
    fontSize: 21,
    fontWeight: 700,
    color: "#0b0b0b",
    letterSpacing: "-0.02em",
  },

  sheetSubtitle: {
    margin: "10px 0 14px",
    fontSize: 14,
    color: "rgba(17,17,17,0.58)",
    lineHeight: 1.5,
    fontWeight: 430,
    letterSpacing: "-0.01em",
  },

  points: {
    textAlign: "left",
    width: "100%",
    maxWidth: 340,
    margin: "0 auto 18px",
    padding: "0 8px 0 24px",
    fontSize: 13,
    color: "rgba(17,17,17,0.78)",
    lineHeight: 1.55,
    fontWeight: 430,
    letterSpacing: "-0.006em",
  },

  actionButton: {
    width: "100%",
    padding: "12px 0",
    background: "#000",
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
    border: "1px solid #000",
    borderRadius: 10,
    cursor: "pointer",
    marginBottom: 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    letterSpacing: "-0.005em",
  },

  mailIcon: {
    fontSize: 16,
    marginRight: 8,
  },

  dismissButton: {
    width: "100%",
    padding: "12px 0",
    background: "#fff",
    color: "#0b0b0b",
    fontSize: 12,
    fontWeight: 650,
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    cursor: "pointer",
    letterSpacing: "-0.005em",
  },
};
